import React, { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import { toast } from "react-toastify";

const displayRideId = (row) => {
  if (row?.rideRequestId) return String(row.rideRequestId);
  if (row?.rideId) return String(row.rideId);
  return "N/A";
};

const formatDatePart = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatTimePart = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const resolveSubject = (subject, customer) => {
  if (subject?.id) {
    return subject;
  }
  if (customer) {
    return {
      id: customer.userId || customer.driverId,
      name: customer.customerName || customer.driverName || "N/A",
      mobile: customer.mobileNumber || "N/A",
    };
  }
  return null;
};

const NumberSharingCallerHistoryModal = ({
  show,
  subject,
  customer,
  sharedParty = "Customer",
  historyUrl,
  onClose,
  onRideClick,
}) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const resolvedSubject = resolveSubject(subject, customer);
  const historyUrlWithParty =
    historyUrl && sharedParty
      ? `${historyUrl}${historyUrl.includes("?") ? "&" : "?"}party=${encodeURIComponent(sharedParty)}`
      : historyUrl;

  useEffect(() => {
    if (!show || !historyUrlWithParty) {
      setHistory([]);
      return;
    }

    const fetchHistory = async () => {
      setLoading(true);
      setHistory([]);
      try {
        const response = await axiosInstance.get(historyUrlWithParty);
        if (response.data?.success) {
          const rows = response.data.data.history || [];
          const party = response.data.data.sharedParty || sharedParty;
          setHistory(rows.filter((row) => !party || row.sharedWith === party));
        }
      } catch (error) {
        console.error("Caller history error:", error);
        toast.error("Failed to load caller history");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [show, historyUrlWithParty, sharedParty]);

  if (!show || !resolvedSubject) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Caller ID history</h3>
            <p className="text-sm text-gray-600 mt-1">
              {resolvedSubject.name} · {resolvedSubject.mobile}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FaTimes />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {loading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-500">
              No caller ID records found for Shared with = {sharedParty}.
            </p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Ride ID
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Caller ID
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Shared Times
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.map((row) => {
                  const when = row.sharedAt || row.createdAt;
                  return (
                    <tr key={String(row.rideId)}>
                      <td className="px-4 py-3 text-sm">
                        {onRideClick && row.rideId ? (
                          <button
                            type="button"
                            onClick={() => onRideClick(row.rideId)}
                            className="font-mono text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {displayRideId(row)}
                          </button>
                        ) : (
                          <span className="font-mono text-gray-900">{displayRideId(row)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.callerId || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.sharedTimes || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {formatDatePart(when)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {formatTimePart(when)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default NumberSharingCallerHistoryModal;
